import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { MessageSquare, Share2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import LoadingSpinner from './LoadingSpinner';
import SaveButton from './SaveButton';
import ShareButton from './ShareButton';

interface TopicCardProps {
  topic: any;
  onClick: (topic: any) => void;
  getVoteCount?: (itemId: string, baseCount: number) => number;
  isLoading?: boolean;
  showSaveButton?: boolean;
}

const TopicCard: React.FC<TopicCardProps> = ({ topic, onClick, getVoteCount, isLoading = false, showSaveButton = false }) => {
  return (
    <div 
      className={`relative bg-gradient-to-br from-gray-800/80 via-gray-900/90 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 shadow-2xl overflow-hidden cursor-pointer hover:border-[#0FFF55]/20 transition-all duration-300 ${
        isLoading ? 'opacity-75 pointer-events-none' : ''
      }`}
      onClick={() => !isLoading && onClick(topic)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#0FFF55]/5 via-transparent to-[#0FFF55]/5"></div>
      <div className="relative p-6">
        <div className="flex items-start gap-4">
          <Avatar className="w-10 h-10 flex-shrink-0">
            <AvatarImage src={topic.author?.avatar_url} />
            <AvatarFallback className="bg-[#0FFF55] text-[#121316] font-semibold">
              {topic.author?.full_name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-white hover:text-[#0FFF55] transition-colors">
                {topic.title}
              </h3>
                                            <div className="flex items-center gap-2">
                 {isLoading && (
                   <LoadingSpinner size="sm" color="primary" />
                 )}
                 {topic.is_pinned && (
                   <Badge variant="secondary" className="bg-yellow-500 text-yellow-900 text-xs">
                     Fixado
                   </Badge>
                 )}
                 {topic.is_locked && (
                   <Badge variant="secondary" className="bg-red-500 text-red-900 text-xs">
                     Bloqueado
                   </Badge>
                 )}
                 {showSaveButton && (
                   <SaveButton topicId={topic.id} size="sm" />
                 )}
                 <ShareButton 
                   topicId={topic.id} 
                   topicTitle={topic.title}
                   categoryId={topic.category_id}
                   size="sm" 
                 />
               </div>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
              <span>{topic.author?.full_name || 'Usuário'}</span>
              <span>•</span>
              <span>
                {topic.created_at && formatDistanceToNow(new Date(topic.created_at), { 
                  addSuffix: true, 
                  locale: ptBR 
                })}
              </span>
              <span>•</span>
              <div className="flex items-center gap-1">
                <MessageSquare className="w-4 h-4" />
                <span>{topic.reply_count || 0} respostas</span>
              </div>
              {getVoteCount && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <span>❤️ {getVoteCount(topic.id, topic.votes_count || 0)}</span>
                  </span>
                </>
              )}
            </div>
            
            <p className="text-gray-300 line-clamp-2">
              {topic.content}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicCard; 